import {FC, useState, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {TextInput} from "@interstate/components/TextInput";
import {
  SelectInput,
  SelectInputEventValue
} from "@interstate/components/SelectInput";
import {TextArea} from "@interstate/components/TextArea";
import {
  FormData,
  ISSUE_TYPE_KEYS,
  PLACEHOLDER_KEY_MAP,
  INITIAL_FORM_STATE,
  BOID_RELATED_ISSUE_KEYS,
  ValidationErrors,
  MIN_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  VALIDATION_ERROR_KEYS,
  BUSINESS_UNITS_TO_SFX_NAME_MAP
} from "./contactSupportFormConfig";
import "./ContactSupportForm.scss";
import {Button} from "@interstate/components/Button";
import {useSubject} from "../../../access";
import {
  businessOperationTypes,
  SupportIssueType,
  SupportRequestInput
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {InterstateOnChangeEvent} from "@interstate/components/InterstateEvents/Types/InterstateInputEventTypes";
import {useGraphqlMutation} from "../../../amplify/hooks";
import {submitSupportRequest as submitSupportRequestMutation} from "../../../amplify/graphql";

import {
  SubmitSupportRequestData,
  SubmitSupportRequestVariables
} from "../../backend";
import {useAlert} from "../../../alerting";
import {Alert} from "@interstate/components/Alert";
import {useFeedback} from "../../../feedback";

interface ContactSupportFormProps {
  dbaName: string;
  commonOrgId: string;
  hideSupportForm: () => void;
}

export const ContactSupportForm: FC<ContactSupportFormProps> = ({
  dbaName,
  commonOrgId,
  hideSupportForm
}) => {
  const {t} = useTranslation();
  const subject = useSubject();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [, setFeedback] = useFeedback();
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_STATE);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const validateForm = useCallback((): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.issueType) {
      errors.issueType = t(VALIDATION_ERROR_KEYS.ISSUE_TYPE_REQUIRED);
    }

    if (!formData.description || formData.description.trim() === "") {
      errors.description = t(VALIDATION_ERROR_KEYS.DESCRIPTION_REQUIRED);
    } else {
      const trimmedDescription = formData.description.trim();
      if (trimmedDescription.length < MIN_DESCRIPTION_LENGTH) {
        errors.description = t(VALIDATION_ERROR_KEYS.DESCRIPTION_TOO_SHORT);
      } else if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
        errors.description = t(VALIDATION_ERROR_KEYS.DESCRIPTION_TOO_LONG);
      }
    }

    if (formData.shouldIncludeBusinessUnit) {
      if (!formData.businessUnit || formData.businessUnit.trim() === "") {
        errors.businessUnit = t(VALIDATION_ERROR_KEYS.BUSINESS_UNIT_REQUIRED);
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleFieldChange = useCallback(
    (field: keyof FormData) =>
      (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
        setFormData(prev => ({...prev, [field]: event.target.value}));
        if (field in validationErrors) {
          setValidationErrors(prev => ({...prev, [field]: undefined}));
        }
      },
    [validationErrors]
  );

  const handleIssueTypeChange = useCallback(
    (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
      const newIssueType = event.target.value as SupportIssueType;
      const isBoidRelated = (
        BOID_RELATED_ISSUE_KEYS as readonly string[]
      ).includes(newIssueType);

      setFormData(prev => {
        return {
          ...prev,
          issueType: newIssueType,
          shouldIncludeBusinessUnit: isBoidRelated,
          businessUnit: isBoidRelated ? prev.businessUnit : undefined
        };
      });

      setValidationErrors(prev => ({
        ...prev,
        issueType: undefined,
        description: undefined,
        businessUnit: undefined
      }));
    },
    []
  );

  const [submitSupportRequest, {loading: isSubmitting}] = useGraphqlMutation<
    SubmitSupportRequestData,
    SubmitSupportRequestVariables
  >({
    amplifyMutation: submitSupportRequestMutation
  });

  const submitSupportForm = async ({
    commonOrgId,
    dbaName,
    issueType,
    businessUnit,
    description,
    resourcePath
  }: SupportRequestInput) => {
    const result = await submitSupportRequest({
      variables: {
        input: {
          commonOrgId,
          dbaName,
          issueType,
          description,
          resourcePath,
          businessUnit: businessUnit || ""
        }
      }
    });
    // Check for GraphQL errors or unsuccessful response
    // result.data will be null if there are GraphQL errors
    if (!result?.data || !result?.data?.submitSupportRequest?.messageId) {
      throw new Error(t("contact-support.errors.submission-failed"));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await submitSupportForm({
        commonOrgId,
        dbaName,
        issueType: formData.issueType!,
        description: formData.description!.trim(),
        businessUnit:
          (formData.businessUnit &&
            BUSINESS_UNITS_TO_SFX_NAME_MAP[formData.businessUnit]) ||
          "",
        resourcePath: window.location.pathname
      });

      setFeedback({
        type: "success",
        message: t("contact-support.submission.success-message"),
        autoHide: true,
        onClose: () => setFeedback(undefined)
      });

      hideSupportForm();
    } catch (error) {
      createErrorAlert(error);
      return;
    }
  };

  const descriptionPlaceholder = () => {
    const placeholderKey =
      formData.issueType && PLACEHOLDER_KEY_MAP[formData.issueType];
    return placeholderKey
      ? t(placeholderKey)
      : t("contact-support.placeholders.default");
  };

  const businessUnitOptions = useMemo(() => {
    return businessOperationTypes()
      .map(boid => ({
        label: t(`entity-detail.business-operation-types.${boid}`),
        value: boid
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [t]);

  const issueTypeOptions = useMemo(
    () =>
      Object.keys(ISSUE_TYPE_KEYS).map(key => ({
        label: t(ISSUE_TYPE_KEYS[key as keyof typeof ISSUE_TYPE_KEYS]),
        value: key
      })),
    [t]
  );

  return (
    <div className="contact-support-form">
      {alert && (
        <Alert
          id="contact-support-form-alert"
          data-testid="contact-support-form-alert"
          onCloseClick={clearAlert}
          title={t(alert.title || "toast.backend-error")}
          type={alert.type}>
          <p>{alert.content()}</p>
        </Alert>
      )}
      <p className="contact-support-form__intro">
        {t("contact-support.intro")}
      </p>

      <div className="contact-support-form__field">
        <TextInput
          data-testid="dba-name-input"
          name="dbaName"
          label={t("contact-support.fields.dba-name")}
          value={dbaName}
          disabled
        />
      </div>

      <div className="contact-support-form__field">
        <TextInput
          data-testid="common-org-id-input"
          name="commonOrgId"
          label={t("contact-support.fields.common-org-id")}
          value={commonOrgId}
          disabled
        />
      </div>

      <div className="contact-support-form__field">
        <SelectInput
          data-testid="issue-type-select"
          name="issueType"
          label={t("contact-support.fields.issue-type")}
          value={formData.issueType || ""}
          onChange={handleIssueTypeChange}
          options={issueTypeOptions}
          placeholder={t("contact-support.placeholders.select")}
          hasError={!!validationErrors.issueType}
          errorMessage={validationErrors.issueType}
          required
        />
      </div>

      {formData.shouldIncludeBusinessUnit && (
        <div className="contact-support-form__field">
          <SelectInput
            data-testid="business-unit-select"
            name="businessUnit"
            label={t("contact-support.fields.business-unit")}
            value={formData.businessUnit || ""}
            onChange={handleFieldChange("businessUnit")}
            options={businessUnitOptions}
            hasError={!!validationErrors.businessUnit}
            errorMessage={validationErrors.businessUnit}
            required
          />
        </div>
      )}

      <div className="contact-support-form__field">
        <TextArea
          data-testid="description-textarea"
          name="description"
          label={t("contact-support.fields.description")}
          value={formData.description || ""}
          onChange={handleFieldChange("description")}
          placeholder={descriptionPlaceholder()}
          rows={8}
          maxLength={MAX_DESCRIPTION_LENGTH}
          hasError={!!validationErrors.description}
          errorMessage={validationErrors.description}
          required
        />
      </div>

      <div className={"footer"}>
        <Button
          id="form-submission-button"
          data-testid="form-submission-button"
          className={"form-submission-button"}
          size={"medium"}
          buttonStyle={"tertiary"}
          onClick={handleSubmit}
          disabled={subject.subject.platformId === "anonymous" || isSubmitting}>
          {t("contact-support.submit")}
        </Button>
        <Button
          id="form-cancel-button"
          data-testid="form-cancel-button"
          className={"form-cancel-button"}
          size={"medium"}
          onClick={hideSupportForm}
          disabled={isSubmitting}>
          {t("contact-support.cancel")}
        </Button>
      </div>
    </div>
  );
};
